import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import AdventureChat from "@/components/adventures/AdventureChat";
import { MemberPageScaffold } from "@/components/MemberScaffold";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { formatDateRange } from "@/lib/adventure-helpers";
import {
  AppButton,
  AppEmptyState,
  AppErrorState,
  AppLoadingState,
} from "../../ui-foundation";

type AdventureSummary = Pick<
  Tables<"adventures">,
  "id" | "user_id" | "title" | "location" | "date_start" | "date_end" | "created_at"
>;

const AdventureChatView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [adventure, setAdventure] = useState<AdventureSummary | null>(null);
  const [accessStartedAt, setAccessStartedAt] = useState<string | null>(null);
  const [canAccess, setCanAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id || !user) return;

    let active = true;
    const load = async () => {
      setLoading(true);
      const [{ data: nextAdventure }, { data: membership }] = await Promise.all([
        supabase
          .from("adventures")
          .select("id, user_id, title, location, date_start, date_end, created_at")
          .eq("id", id)
          .eq("status", "active")
          .maybeSingle(),
        supabase
          .from("adventure_members")
          .select("status, created_at")
          .eq("adventure_id", id)
          .eq("user_id", user.id)
          .maybeSingle(),
      ]);

      if (!active) return;

      const nextCanAccess =
        nextAdventure?.user_id === user.id || membership?.status === "joined";
      setAdventure((nextAdventure as AdventureSummary | null) ?? null);
      setCanAccess(nextCanAccess);
      setAccessStartedAt(membership?.created_at ?? nextAdventure?.created_at ?? null);
      setLoading(false);
    };

    void load();
    return () => {
      active = false;
    };
  }, [id, user]);

  const fallbackPath = id ? `/adventures/${id}` : "/activities";

  if (loading) {
    return (
      <MemberPageScaffold title="Activity chat" fallbackPath={fallbackPath}>
        <AppLoadingState title="Loading chat" body="Opening the latest Activity conversation." />
      </MemberPageScaffold>
    );
  }

  if (!adventure) {
    return (
      <MemberPageScaffold title="Activity chat" fallbackPath="/activities">
        <AppErrorState
          title="Chat not found"
          body="The Activity may have been removed or the link may be out of date."
        />
      </MemberPageScaffold>
    );
  }

  if (!canAccess) {
    return (
      <MemberPageScaffold
        title={adventure.title}
        subtitle="Activity chat"
        fallbackPath={`/adventures/${adventure.id}`}
      >
        <AppEmptyState
          title="Join the Activity to open its chat"
          body="The organiser and people who are going can read and reply here."
          action={
            <AppButton
              type="button"
              tone="outline"
              onPress={() => navigate(`/adventures/${adventure.id}`)}
            >
              View Activity
            </AppButton>
          }
        />
      </MemberPageScaffold>
    );
  }

  return (
    <MemberPageScaffold
      title={adventure.title}
      subtitle={`${formatDateRange(adventure.date_start, adventure.date_end)} · ${adventure.location}`}
      fallbackPath={`/adventures/${adventure.id}`}
      titleLines={2}
    >
      <AdventureChat adventureId={adventure.id} accessStartedAt={accessStartedAt} />
    </MemberPageScaffold>
  );
};

export default AdventureChatView;
